import React, { useContext, useEffect, useState } from 'react'
import {
	FormBuilder,
	formKeySelector,
	validateFormDataAction,
} from 'formBuilder/src'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import {
	ApplicationDispatchContext,
	LOAN_APPLICATION_ACTIONS,
} from 'contexts/ApplicationContext'
import { IApplicationInformation } from 'interfaces/i-application-information'
import { getLanguageLocale, getMarket } from 'utils/countryHelpers'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { FormFactory } from 'formBuilder/formFactory'
import { CustomerForm } from 'constants/enums/formTypes'

import './FillPersonalDetail.scss'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'
import { MSP_ACTIONS, MspDispatchContext } from 'components/multistep/Multistep'
import updateApplicationServices from 'services/updateApplicationServices'

export const FillPersonalDataLt = () => {
	const { t } = useTranslation()
	const { loanApplication, dispatchLoanApplication }: IApplicationContext =
		useContext(ApplicationDispatchContext)
	const { msp, dispatchMsp } = useContext(MspDispatchContext)
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<Error | any>()
	const applicationId = loanApplication.applicationData?.loanNumber!

	const dispatch = useDispatch()

	let bankInfoFormSchema = FormFactory(
		CustomerForm.ApplicationBankInfoForm,
		{
			formName: 'bankInfoForm',
			blockName: 'bankInfoForm',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation
	)

	const bankInfoFormStateValues = useSelector(
		formKeySelector(bankInfoFormSchema.formMeta.formName, 'controlsState')
	)

	let addressFormSchema = FormFactory(
		CustomerForm.ApplicationAddressForm,
		{
			formName: 'addressForm',
			blockName: 'addressForm',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation
	)

	const addressFormStateValues = useSelector(
		formKeySelector(addressFormSchema.formMeta.formName, 'controlsState')
	)

	const updatePersonalInfo = (
		iban: string | undefined,
		beneficialOwner: boolean | undefined,
		pep: boolean | undefined,
		purposeOfLoan: string | undefined,
		street: string | undefined,
		city: string | undefined,
		zip: string | undefined,
		country: string | undefined,
		citizenship: string
	) => {
		dispatchLoanApplication({
			type: LOAN_APPLICATION_ACTIONS.SET_UPDATE_APPLICATIONDATA,
			payload: {
				...loanApplication,
				updateApplicationData: {
					...loanApplication.updateApplicationData,
					bankInfo: {
						...loanApplication.updateApplicationData?.bankInfo,
						iban: iban?.replaceAll(' ', '').toUpperCase(),
					},
					applicant: {
						...loanApplication.updateApplicationData?.applicant,
						additionalInfo: {
							...loanApplication.updateApplicationData?.applicant
								?.additionalInfo,
							beneficialOwner: beneficialOwner,
							politicallyExposedPerson: pep,
						},
						personalInfo: {
							...loanApplication.updateApplicationData?.applicant
								?.personalInfo,
							firstName:
								loanApplication.applicationData?.personalInfo
									?.firstName,
							lastName:
								loanApplication.applicationData?.personalInfo
									?.lastName,
							citizenship: [citizenship],
						},
						address: {
							...loanApplication.updateApplicationData?.applicant
								?.address,
							street: street,
							city: city,
							zip: zip,
							countryOfResidence: country,
						},
					},
					purposeOfLoan: purposeOfLoan,
				},
			},
		})
	}

	useEffect(
		() => {
			updatePersonalInfo(
				bankInfoFormStateValues?.ibanVal.value,
				bankInfoFormStateValues?.beneficialOwner.value,
				bankInfoFormStateValues?.pep.value,
				bankInfoFormStateValues?.purposeOfLoan.visible
					? bankInfoFormStateValues?.purposeOfLoan.value
					: null,
				addressFormStateValues?.street.value,
				addressFormStateValues?.city.value,
				addressFormStateValues?.zip.value,
				addressFormStateValues?.countryOfResidence.value,
				addressFormStateValues?.citizenship.value
			)
		},
		[bankInfoFormStateValues, addressFormStateValues] // eslint-disable-line react-hooks/exhaustive-deps
	)

	const updatePersonalData = async () => {
		dispatch(validateFormDataAction('bankInfoForm'))
		dispatch(validateFormDataAction('addressForm'))

		if (
			!bankInfoFormStateValues?.ibanVal.isValid ||
			!addressFormStateValues?.street.isValid ||
			!addressFormStateValues?.zip.isValid ||
			!addressFormStateValues?.city.isValid ||
			!addressFormStateValues?.countryOfResidence.isValid ||
			!addressFormStateValues?.citizenship.isValid
		) {
			return
		}

		setLoading(true)
		setError(null)
		try {
			await updateApplicationServices
				.updateLoanApplicationPersonalData(
					applicationId,
					loanApplication.updateApplicationData!,
					t
				)
				.then(async (response) => {
					if (response.status === 200) {
						dispatchMsp({
							type: MSP_ACTIONS.SET_INDEX,
							payload: {
								index: msp.index + 1,
							},
						})
					}
				})
		} catch (error) {
			console.warn(error)
			setError(error)
			setLoading(false)
		}
	}

	return (
		<div className="update-personal-data">
			{loading ? (
				<div className="col-spinner">
					<CircleSpinner />
				</div>
			) : (
				<>
					{error ? (
						<ErrorBox message={t('ErrorMsg')} />
					) : (
						<>
							<div className="update-personal-data-section">
								{addressFormSchema ? (
									<FormBuilder
										formSchema={addressFormSchema}
										apiError={undefined}
										showErrorsInForm={true}
										showSubmitButton={false}
									/>
								) : (
									<CircleSpinner />
								)}
							</div>
							<div className="update-personal-data-section">
								{bankInfoFormSchema ? (
									<FormBuilder
										formSchema={bankInfoFormSchema}
										apiError={undefined}
										showErrorsInForm={true}
										showSubmitButton={false}
									/>
								) : (
									<CircleSpinner />
								)}
							</div>
							<div className="update-personal-data-next-button">
								<button
									type="button"
									disabled={loading}
									className="btn btn-primary"
									onClick={() => {
										setTimeout(() => {
											updatePersonalData()
										}, 400)
									}}>
									{t('ContractReview')}
								</button>
							</div>
						</>
					)}
				</>
			)}
		</div>
	)
}